// @angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";

// Translate
import { TranslateModule, TranslateService } from "@ngx-translate/core";
// Project services
import { ReloadComponentService } from "../../../../services/shared/ReloadComponentService.service";
import { ToastService } from "../../../../services/shared/ToastService.service";
import { MapSizeService } from "../../../../modules/map-module/map-services/MapSizeService.service";
import { MaterialDialogService } from "../../../../modules/material-module/material-dialog/material-dialog.service";
import { SessionDataService } from "../../../../services/shared/SessionDataService.service";
import { MeterControllerService } from "../../../../services/server/MeterController.service";
// Interfaces
import { ActivationData } from "../installation-interface";
import { DeviceActivationData } from "../../devices/DeviceInterface.type";
import { PROFILES } from "../../../../../assets/profiles/profiles";
import { MAP_TYPES_NAMES } from "../../../../modules/map-module/map-variables/MAP_TYPES";
// Components
import { MaterialPanelMenuComponent } from "../../../../modules/material-module/material-panel-menu/material-panel-menu.component";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MapControllerComponent } from "../../../../modules/map-module/map-controller/map-controller.component";
import { ActivationModalComponent } from "./activation-modal/activation-modal.component";
import { PanelBackComponent } from "../../../../modules/nav-module/panel-back/panel-back.component";

/** Lets an installer activate a meter with its identifiers, location, and installation photos. */
@Component({
  selector: "app-installation-activation",
  templateUrl: "./installation-activation.component.html",
  styleUrls: ["./installation-activation.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelBackComponent,
    MapControllerComponent,
    MaterialPanelMenuComponent,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    TranslateModule
  ]
})
export class InstallationActivationComponent implements OnInit, OnDestroy, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  saveIcon: string = "fas fa-save";
  saveTitle: string = this.translate.instant("activate");
  sessionProfile: string;

  @ViewChild("activationPanel") activationPanel: ElementRef;
  activationData: ActivationData = new ActivationData();
  mapType: string = MAP_TYPES_NAMES.CHANGE_LOCATION;
  mapHeight: number = 400;
  initialZoom: number = 17;
  @ViewChild(MapControllerComponent) mapController: MapControllerComponent;
  currentScanning: string;
  readonly PROFILES = PROFILES;

  // CUPS
  cupsName: string;

  // Listen for window resize to resize the map
  @HostListener("window:resize")
  onResize() {
    this.resizeMap();
  }

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly MaterialDialogService: MaterialDialogService,
    private readonly MapSizeService: MapSizeService,
    private readonly MeterController: MeterControllerService,
    private readonly ReloadComponentService: ReloadComponentService,
    private readonly SessionDataService: SessionDataService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {
    effect(() => {
      const entityDefinitionData = this.SessionDataService.entityCupsConfParsed();
      if (entityDefinitionData !== null) {
        this.cupsName = entityDefinitionData?.entityCups?.name;
      }
    });
    effect(() => {
      const data = this.SessionDataService.componentData();
      if (data !== null) {
        if (data.webcamImage) {
          this.activationData.photos.push({
            file: this.mapController.parseBase64ToFile(
              data.webcamImage.imageAsBase64,
              "photo",
              "image/jpeg"
            ),
            image: data.webcamImage.imageAsDataUrl
          });
        }

        if (data.scannerResult) {
          switch (this.currentScanning) {
            case "serialNumber":
              this.activationData.serialNumber = data.scannerResult;
              break;
            case "rfModule":
              this.activationData.rfModule = data.scannerResult;
              break;
            case "comments":
              this.activationData.comments = data.scannerResult;
              break;
            default:
              break;
          }
        }
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.sessionProfile = untracked(() => this.SessionDataService.profile());

    // Fetch coordinates
    this.getLocation();
  }

  /***************************************************************************/
  // ANCHOR Render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeMap(), 0);
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Resize map to match card visibility
  resizeMap(): void {
    this.mapHeight =
      window.innerWidth > 980
        ? this.MapSizeService.calcMapHeight(this.activationPanel, null, 0)
        : 400;
  }

  // Map update
  updateMap(): void {
    this.activationData = { ...this.activationData };
  }

  setNewLocation(location): void {
    this.activationData.latitude = location.lat;
    this.activationData.longitude = location.lng;
  }

  geolocationError(_error?): void {
    this.ToastService.fireToast("error", this.translate.instant("geolocation-error"));
  }

  // Entity image file change
  fileChangeEvent(event: any): void {
    this.activationData.photos.push({ file: event.target.files[0] });
    this.getImagePreview(this.activationData.photos[this.activationData.photos.length - 1]);
  }

  // Logo preview fetch
  getImagePreview(photo: any): void {
    let reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        photo.image = reader.result;
      },
      false
    );
    if (photo.file) {
      reader.readAsDataURL(photo.file);
    }
  }

  // Photo deletion
  deletePhoto(photoIndex: number): void {
    this.activationData.photos.splice(photoIndex, 1);
  }

  // Show camera
  showCamera(): void {
    this.MaterialDialogService.openDialog(ActivationModalComponent, {
      camera: true
    });
  }

  // Show scanner
  showScanner(input: string): void {
    this.currentScanning = input;
    this.MaterialDialogService.openDialog(ActivationModalComponent, {
      scanner: true
    });
  }

  // Activate meter
  activateMeter(): void {
    this.ToastService.fireAlertWithOptions(
      "question",
      this.translate.instant("meter-question-activate")
    ).then((userConfirmation: boolean) => {
      if (userConfirmation) {
        let data: DeviceActivationData[] = [
          {
            nroSerie: this.activationData.serialNumber,
            latitude: this.activationData.latitude,
            longitude: this.activationData.longitude,
            entity: untracked(() => this.SessionDataService.entity()).entity,
            agrupation: untracked(() => this.SessionDataService.agrupation()).name,
            rfModule: this.activationData.rfModule !== "" ? this.activationData.rfModule : null,
            comments: this.activationData.comments,
            precinto: this.activationData.seal,
            cups: this.activationData.cups
          }
        ];
        this.MeterController.activate(data).subscribe(response => {
          if (response["code"] === 0) {
            this.ToastService.fireToast(
              "success",
              this.translate.instant("success-meter-activate")
            );
            if (this.activationData.photos.length > 0) {
              this.uploadImage(this.activationData.photos[0].file, 0);
            }
          } else if (response["body"] && response["body"][0]?.responseCode !== 0) {
            this.ToastService.fireToast(
              "error",
              this.translate.instant("_http_error." + response["body"][0].responseCode)
            );
          }
        });
      }
    });
  }

  // Meter installation image
  uploadImage(photo, index: number): void {
    this.MeterController.activationImageUpload(this.activationData.serialNumber, photo).subscribe(
      _response => {
        index++;
        if (index < this.activationData.photos.length) {
          this.uploadImage(this.activationData.photos[index].file, index);
        } else {
          this.ReloadComponentService.reload();
        }
      }
    );
  }

  // Location fetch
  getLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        if (position?.coords?.latitude != null && position?.coords?.longitude != null) {
          this.activationData.latitude = position.coords.latitude;
          this.activationData.longitude = position.coords.longitude;
          this.activationData = { ...this.activationData };
        } else {
          this.geolocationError();
        }
      });
    } else {
      this.geolocationError();
      // Browser doesn't support Geolocation
    }
  }

  // Data reset
  resetData(): void {
    this.activationData = new ActivationData();
    this.getLocation();
  }
}
